"use client";

import { useEffect, useRef, useState } from "react";
import { sameOriginArtifactUrl } from "@/utils/artifactUrl";
import { base64ToBytes } from "@/utils/fileTypeDetection";

export type PreviewFetchState<T> = {
   data: T | null;
   error: string | null;
   loading: boolean;
};

// A transcript can render many artifact cards at once. Without a limit, every
// card that scrolls into view fans out a full xlsx parse / pdf.js fetch in
// parallel, spiking CPU and network. The gate is module-scoped so all
// `usePreviewFetch` callers share one queue.
const MAX_CONCURRENT_PREVIEW_FETCHES = 3;
let runningPreviewFetches = 0;
const previewFetchQueue: Array<() => void> = [];

const acquirePreviewSlot = (): Promise<void> => {
   if (runningPreviewFetches < MAX_CONCURRENT_PREVIEW_FETCHES) {
      runningPreviewFetches++;
      return Promise.resolve();
   }
   return new Promise<void>((resolve) => {
      previewFetchQueue.push(() => {
         runningPreviewFetches++;
         resolve();
      });
   });
};

const releasePreviewSlot = (): void => {
   runningPreviewFetches--;
   const next = previewFetchQueue.shift();
   if (next) next();
};

/** @internal — for tests. */
export const __resetPreviewFetchGateForTests = (): void => {
   runningPreviewFetches = 0;
   previewFetchQueue.length = 0;
};

/**
 * Fetches a remote artifact and decodes it into a preview-ready shape. Handles
 * cancellation on unmount / url change so late responses can't update a
 * dismissed preview, and surfaces `error` so the caller can render a fallback
 * instead of spinning forever.
 *
 * Calls are gated through a shared concurrency queue so a scroll-burst of
 * artifact cards can't stampede the network or CPU.
 *
 * The `transform` callback runs inside the effect; it is captured by ref so
 * inline lambdas don't cause the fetch to re-run on every render.
 */
export function usePreviewFetch<T>(
   url: string | undefined,
   transform: (res: Response) => Promise<T>
): PreviewFetchState<T> {
   const [data, setData] = useState<T | null>(null);
   const [error, setError] = useState<string | null>(null);
   const transformRef = useRef(transform);
   transformRef.current = transform;

   useEffect(() => {
      if (!url) {
         setData(null);
         setError(null);
         return;
      }
      let cancelled = false;
      let slotAcquired = false;
      const controller = new AbortController();
      setData(null);
      setError(null);
      const load = async () => {
         await acquirePreviewSlot();
         slotAcquired = true;
         // Release-on-cancel must happen unconditionally after acquire; if we
         // bailed on `cancelled` inside the try, the slot leaked and eventually
         // starved the pool.
         try {
            if (cancelled) return;
            // First-party Wingman URLs ride the same-origin signed-in rewrite,
            // so the session cookie stays on our origin and Wingman gets a
            // bearer token. Cross-origin signed URLs (e.g. GCS) pass through
            // unchanged and, being cross-origin, get no cookie with
            // `same-origin` credentials — see `src/utils/artifactUrl.ts`.
            const res = await fetch(sameOriginArtifactUrl(url), {
               credentials: "same-origin",
               signal: controller.signal,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const value = await transformRef.current(res);
            if (!cancelled) setData(value);
         } catch (err) {
            if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
            console.warn("Preview fetch failed:", err);
            setError(err instanceof Error ? err.message : "fetch_failed");
         } finally {
            if (slotAcquired) releasePreviewSlot();
         }
      };
      load();
      return () => {
         cancelled = true;
         controller.abort();
      };
   }, [url]);

   return { data, error, loading: data === null && error === null && !!url };
}

/** Creates and manages a blob URL from raw data, with automatic cleanup. */
export const useBlobUrl = (data: string | undefined, mimeType: string): string | null => {
   const [url, setUrl] = useState<string | null>(null);

   useEffect(() => {
      if (!data) return;
      const bytes = base64ToBytes(data) as Uint8Array<ArrayBuffer>;
      const blob = new Blob([bytes], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      setUrl(blobUrl);
      return () => URL.revokeObjectURL(blobUrl);
   }, [data, mimeType]);

   return url;
};

/** Creates a blob URL from a File object, with automatic cleanup. */
export const useObjectUrl = (file: File | undefined, enabled: boolean): string | null => {
   const [url, setUrl] = useState<string | null>(null);

   useEffect(() => {
      if (!file || !enabled) return;
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
   }, [file, enabled]);

   return url;
};

const parseXlsxFirstSheet = async (
   source: Uint8Array | ArrayBuffer
): Promise<string[][]> => {
   const XLSX = await import("@e965/xlsx");
   const wb = XLSX.read(source, {type: "array"});
   const ws = wb.Sheets[wb.SheetNames[0]];
   return XLSX.utils.sheet_to_json<string[]>(ws, {header: 1});
};

/** Parses an Excel file (base64 data) into a 2D string array for thumbnail display. */
export const useExcelData = (data: string | undefined, isExcel: boolean): string[][] | null => {
   const [excelData, setExcelData] = useState<string[][] | null>(null);

   useEffect(() => {
      if (!isExcel || !data) {
         setExcelData(null);
         return;
      }
      let cancelled = false;
      const parse = async () => {
         try {
            const rows = await parseXlsxFirstSheet(base64ToBytes(data));
            if (!cancelled) setExcelData(rows);
         } catch (err) {
            if (!cancelled) {
               console.warn("Excel parse failed:", err);
               setExcelData(null);
            }
         }
      };
      parse();
      return () => {
         cancelled = true;
      };
   }, [data, isExcel]);

   return excelData;
};

/** Parses an Excel File object into a 2D string array for thumbnail display. */
export const useExcelFile = (file: File | undefined, isExcel: boolean): string[][] | null => {
   const [excelData, setExcelData] = useState<string[][] | null>(null);

   useEffect(() => {
      if (!file || !isExcel) {
         setExcelData(null);
         return;
      }
      let cancelled = false;
      const parse = async () => {
         try {
            const buffer = await file.arrayBuffer();
            if (cancelled) return;
            const rows = await parseXlsxFirstSheet(buffer);
            if (!cancelled) setExcelData(rows.slice(0, 8));
         } catch (err) {
            if (!cancelled) {
               console.warn("Excel parse failed:", err);
               setExcelData(null);
            }
         }
      };
      parse();
      return () => {
         cancelled = true;
      };
   }, [file, isExcel]);

   return excelData;
};
